// # Billing Settings Tab Component
import React, { useState, useEffect } from 'react';
import PropTypes from 'prop-types';
import { FaCreditCard, FaHistory, FaSpinner, FaExclamationCircle, FaFileInvoice, FaDownload, FaTimes } from 'react-icons/fa';
import './billing.css';
import { db } from '../../../firebase/config';
import { collection, getDocs, query, where, doc, getDoc } from 'firebase/firestore';
import { useAuthStore } from '../../../auth/state';
import { jsPDF } from "jspdf";
import autoTable from 'jspdf-autotable';
import { getCountryDataByNationality } from '../../../utils/phone_code';

// # Format product name helper function
const formatProductName = (productName) => {
  return productName
    .split('_')
    .map(word => {
      // Special handling for USMLE and numbers
      if (word.toLowerCase() === 'usmle') return 'USMLE';
      if (word.match(/^step[0-9]$/i)) {
        return `Step ${word.slice(4)}`;
      }
      return word.charAt(0).toUpperCase() + word.slice(1).toLowerCase();
    })
    .join(' ');
};

function BillingSettings({ isDarkMode }) {
  // # State management
  const [paymentHistory, setPaymentHistory] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedPayment, setSelectedPayment] = useState(null);
  const [showModal, setShowModal] = useState(false);
  const { user } = useAuthStore();

  // # Fetch payment history on component mount
  useEffect(() => {
    if (user?.uid) {
      fetchPaymentHistory();
    }
  }, [user]);

  // # Function to fetch payment history from Firestore
  const fetchPaymentHistory = async () => {
    try {
      setLoading(true);
      setError(null);
      
      // # Get user profile data to ensure we have phone number and nationality
      const userDoc = await getDoc(doc(db, 'users', user.uid));
      const userData = userDoc.exists() ? userDoc.data() : {};
      const userPhoneNumber = userData.phoneNumber || '';
      const userNationality = userData.nationality || 'EGYPT';
      
      // Get country data based on nationality
      const countryData = getCountryDataByNationality(userNationality);
      const countryCode = countryData?.code || '+20'; // Default to Egypt if not found
      
      const financialCollectionRef = collection(db, 'financial');
      const financialSnapshot = await getDocs(financialCollectionRef);
      
      let userPayments = [];
      
      financialSnapshot.forEach((doc) => {
        const docData = doc.data();
        
        if (docData.payments) {
          Object.entries(docData.payments).forEach(([paymentId, paymentData]) => {
            if (paymentData.user && paymentData.user.userId === user.uid) {
              // # Ensure phone number has country code
              let formattedPhone = paymentData.user.phone || userPhoneNumber;
              if (!formattedPhone.startsWith('+')) {
                formattedPhone = countryCode + (formattedPhone.startsWith('0') ? formattedPhone.slice(1) : formattedPhone);
              }
              
              const paymentUser = {
                ...paymentData.user,
                phone: formattedPhone,
                nationality: paymentData.user.nationality || userNationality
              };
              
              userPayments.push({
                id: paymentId,
                docId: doc.id,
                ...paymentData,
                user: paymentUser,
                formattedDate: new Date(paymentData.metadata.created_at).toLocaleDateString('en-US', {
                  year: 'numeric',
                  month: 'short',
                  day: 'numeric'
                }),
                formattedTime: new Date(paymentData.metadata.created_at).toLocaleTimeString('en-US', {
                  hour: '2-digit',
                  minute: '2-digit'
                })
              });
            }
          });
        }
      });
      
      userPayments.sort((a, b) => 
        new Date(b.metadata.created_at) - new Date(a.metadata.created_at)
      );
      
      setPaymentHistory(userPayments);
      setLoading(false);
    } catch (err) {
      console.error('Error fetching payment history:', err);
      setError('Failed to load payment history. Please try again later.');
      setLoading(false);
    }
  };

  // # Handle invoice button click
  const handleInvoiceClick = (payment) => {
    setSelectedPayment(payment);
    setShowModal(true);
  };

  // # Handle modal close
  const handleCloseModal = () => {
    setShowModal(false);
    setSelectedPayment(null);
  };

  // # Handle invoice download
  const handleDownloadInvoice = () => {
    if (!selectedPayment) return;

    // # Create new PDF document in landscape
    const doc = new jsPDF({
      orientation: 'landscape',
      unit: 'mm',
      format: 'a4'
    });
    
    // # Add logo
    const logoPath = '/logo512.png';
    const img = new Image();
    img.src = logoPath;
    
    // # Load logo and create PDF
    img.onload = () => {
      // # Set document properties
      doc.setProperties({
        title: `TEMP-U Invoice - ${selectedPayment.id}`,
        subject: 'Payment Invoice',
        author: 'TEMP-U',
        keywords: 'invoice, payment, TEMP-U',
        creator: 'TEMP-U Billing System'
      });

      // # Add decorative border
      doc.setDrawColor(147, 197, 253); // Light blue
      doc.setLineWidth(0.5);
      doc.rect(10, 10, 277, 190); // Outer border
      doc.setDrawColor(167, 139, 250); // Light purple
      doc.rect(12, 12, 273, 186); // Inner border

      // # Calculate logo dimensions for header
      const aspectRatio = img.width / img.height;
      const logoWidth = 25;
      const logoHeight = logoWidth / aspectRatio;
      
      // # Add logo centered at top
      doc.addImage(img, 'PNG', (297 - logoWidth) / 2, 20, logoWidth, logoHeight);

      // # Add certificate-style header
      doc.setFont(undefined, 'bold');
      doc.setFontSize(24);
      doc.setTextColor(30, 41, 59);
      doc.text('PAYMENT INVOICE', 148.5, logoHeight + 30, { align: 'center' });

      // # Add organization name
      doc.setFontSize(16);
      doc.setTextColor(52, 152, 219);
      doc.text('The Egyptian Mentorship Program for USMLE', 148.5, logoHeight + 40, { align: 'center' });

      // # Add decorative line under header
      doc.setDrawColor(236, 172, 251); // Light pink
      doc.setLineWidth(0.3);
      doc.line(40, logoHeight + 45, 257, logoHeight + 45);

      // # Add invoice details in a certificate style
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);

      // # Create three-column layout for details
      const startY = logoHeight + 60;
      const colWidth = 85;
      const col1X = 30;
      const col2X = 120;
      const col3X = 210;

      // # Customer Information Column
      doc.setFont(undefined, 'bold');
      doc.setFontSize(14);
      doc.setTextColor(52, 152, 219);
      doc.text('Customer Information', col1X, startY);
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text([
        `Name: ${selectedPayment.user.name || 'N/A'}`,
        `Email: ${selectedPayment.user.email || 'N/A'}`,
        `Phone: ${selectedPayment.user.phone || 'N/A'}`
      ], col1X, startY + 10, { lineHeightFactor: 1.5 });

      // # Product Details Column
      doc.setFont(undefined, 'bold');
      doc.setFontSize(14);
      doc.setTextColor(52, 152, 219);
      doc.text('Product Details', col2X, startY);
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text([
        `Product: ${formatProductName(selectedPayment.pay.product)}`,
        `Duration: ${selectedPayment.pay.duration || 'N/A'}`
      ], col2X, startY + 10, { lineHeightFactor: 1.5 });

      // # Payment Information Column
      doc.setFont(undefined, 'bold');
      doc.setFontSize(14);
      doc.setTextColor(52, 152, 219);
      doc.text('Payment Information', col3X, startY);
      
      doc.setFont(undefined, 'normal');
      doc.setFontSize(12);
      doc.setTextColor(30, 41, 59);
      doc.text([
        `Date: ${selectedPayment.formattedDate}`,
        `Time: ${selectedPayment.formattedTime}`,
        `Amount: ${selectedPayment.pay.currency_symbol} ${selectedPayment.pay.amount_native_currency.toLocaleString()}`,
        `Currency: ${selectedPayment.pay.currency}`
      ], col3X, startY + 10, { lineHeightFactor: 1.5 });

      // # Add thank you message with style
      doc.setFont(undefined, 'bold');
      doc.setFontSize(16);
      doc.setTextColor(34, 197, 94);
      doc.text('Thank You for Your Payment!', 148.5, 160, { align: 'center' });

      // # Add footer
      doc.setFontSize(10);
      doc.setTextColor(100, 116, 139);
      doc.setFont(undefined, 'normal');
      doc.text('This is an automatically generated invoice from TEMP-U.', 148.5, 190, { align: 'center' });
      doc.text('For inquiries: customer-service@wearetempu.com', 148.5, 195, { align: 'center' });

      // # Save the PDF with ISO timestamp
      const timestamp = new Date().toISOString().replace(/[:.]/g, '-');
      doc.save(`Invoice_${timestamp}.pdf`);
    };
  };

  // # Invoice Modal Component with enhanced styling
  const InvoiceModal = ({ payment }) => {
    if (!payment) return null;

    return (
      <div className="blng_st_modal-overlay" onClick={handleCloseModal}>
        <div className="blng_st_modal" onClick={e => e.stopPropagation()}>
          <div className="blng_st_modal-header">
            <img src="/logo512.png" alt="TEMP-U Logo" className="blng_st_modal-logo-img" />
            <div className="blng_st_modal-logo">The Egyptian Mentorship Program for USMLE</div>
            <div className="blng_st_modal-thank-you">Thank You for Your Payment!</div>
          </div>
          
          <div className="blng_st_modal-content">
            <div className="blng_st_modal-section">
              <h4 className="blng_st_modal-section-title">Customer Information</h4>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Name</div>
                <div className="blng_st_modal-value">{payment.user.name || 'N/A'}</div>
              </div>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Email</div>
                <div className="blng_st_modal-value">{payment.user.email || 'N/A'}</div>
              </div>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Phone Number</div>
                <div className="blng_st_modal-value">{payment.user.phone || 'N/A'}</div>
              </div>
            </div>

            <div className="blng_st_modal-section">
              <h4 className="blng_st_modal-section-title">Product Details</h4>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Product</div>
                <div className="blng_st_modal-value">
                  {formatProductName(payment.pay.product)}
                </div>
              </div>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Duration</div>
                <div className="blng_st_modal-value">{payment.pay.duration || 'N/A'}</div>
              </div>
            </div>

            <div className="blng_st_modal-section">
              <h4 className="blng_st_modal-section-title">Payment Information</h4>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Payment Date & Time</div>
                <div className="blng_st_modal-value">
                  {payment.formattedDate} | {payment.formattedTime}
                </div>
              </div>
              <div className="blng_st_modal-row">
                <div className="blng_st_modal-label">Amount</div>
                <div className="blng_st_modal-value">
                  {payment.pay.currency_symbol} {payment.pay.amount_native_currency.toLocaleString()} {payment.pay.currency}
                </div>
              </div>
            </div>
          </div>

          <div className="blng_st_modal-actions">
            <button className="blng_st_modal-btn blng_st_modal-close" onClick={handleCloseModal}>
              <FaTimes /> Close
            </button>
            <button className="blng_st_modal-btn blng_st_modal-download" onClick={handleDownloadInvoice}>
              <FaDownload /> Download PDF
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className="blng_st_section">
      <div className="blng_st_header">
        <h2>Billing & Payments</h2>
      </div>

      <div className="blng_st_content">
        {/* # Payment History Section */}
        <div className="blng_st_payment-history">
          <div className="blng_st_payment-history-header">
            <h3><FaHistory className="blng_st_history-icon" /> Payment History</h3>
          </div>
          
          {loading ? (
            <div className="blng_st_loading">
              <FaSpinner className="blng_st_spinner" />
              <p>Loading payment history...</p>
            </div>
          ) : error ? (
            <div className="blng_st_error">
              <FaExclamationCircle className="blng_st_error-icon" />
              <p>{error}</p>
            </div>
          ) : paymentHistory.length === 0 ? (
            <div className="blng_st_no-payments">
              <p>No payment records found.</p>
            </div>
          ) : (
            <div className="blng_st_payment-table">
              <div className="blng_st_payment-table-header">
                <div className="blng_st_payment-cell">Product</div>
                <div className="blng_st_payment-cell">Amount</div>
                <div className="blng_st_payment-cell">Currency</div>
                <div className="blng_st_payment-cell">Date</div>
              </div>
              
              {/* # Payment rows */}
              {paymentHistory.map((payment) => (
                <div key={payment.id} className="blng_st_payment-row">
                  <div className="blng_st_payment-cell blng_st_product">
                    {payment.pay.product.replace(/_/g, ' ').replace(/(\w+)/, match => 
                      match.charAt(0).toUpperCase() + match.slice(1)
                    )}
                    {payment.pay.duration && ` (${payment.pay.duration})`}
                  </div>
                  <div className="blng_st_payment-cell blng_st_amount">
                    {payment.pay.currency_symbol} {payment.pay.amount_native_currency.toLocaleString()}
                  </div>
                  <div className="blng_st_payment-cell blng_st_currency">
                    {payment.pay.currency}
                  </div>
                  <div className="blng_st_payment-cell blng_st_date">
                    {payment.formattedDate}
                    <button 
                      className="blng_st_invoice-btn"
                      onClick={() => handleInvoiceClick(payment)}
                      title="View Invoice"
                    >
                      <FaFileInvoice className="blng_st_invoice-icon" />
                    </button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        
        {/* # Invoice Modal */}
        {showModal && <InvoiceModal payment={selectedPayment} />}
        
        {/* # Coming Soon Features Section */}
        <div className="blng_st_coming-soon">
          <FaCreditCard className="blng_st_icon" />
          <h3>More Billing Features Coming Soon</h3>
          <p>We're working on enhanced billing and payment features.</p>
          <p>Features will include:</p>
          <ul>
            <li>Payment method management</li>
            <li>Billing notifications</li>
          </ul>
        </div>
      </div>
    </div>
  );
}

BillingSettings.propTypes = {
  isDarkMode: PropTypes.bool
};

BillingSettings.defaultProps = {
  isDarkMode: false
};

export default BillingSettings;
